/**
 * App shell — top bar with brand and user menu.
 *
 * The shell stays consistent across all authenticated views; only the
 * `<Outlet />` slot changes when navigating. The household switcher
 * lives in the per-page breadcrumb (HouseholdBreadcrumb), not here.
 */

import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useId, useState } from "react";

import { t } from "../i18n";
import { profileSchema } from "../lib/schemas";
import { type Currency, type ThemePref, useCurrency, useTheme } from "../lib/settings";
import { trpc } from "../lib/trpc";
import { useUserConnection } from "../lib/use-user-connection";
import { fieldError } from "../lib/validation";
import { ConnectionStatusToast } from "./ConnectionStatusToast";
import { Footer } from "./Footer";
import { InvitationToastBridge } from "./InvitationToastBridge";
import { MyCardsContent } from "./MyCardsPanel";
import {
  Avatar,
  Button,
  Container,
  Dialog,
  DropdownMenu,
  Field,
  FieldMessage,
  Icon,
  Input,
  MenuItem,
  MenuSeparator,
  Select,
  TapTarget,
  useToast,
} from "./ui";

export interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  // Single per-tab realtime connection. AppShell only renders for
  // authenticated users (gated in __root.tsx), so mounting here
  // unconditionally is safe — no per-page hook plumbing needed.
  const { status } = useUserConnection();

  return (
    <>
      <InvitationToastBridge />
      <ConnectionStatusToast status={status} />
      <div className="flex min-h-screen flex-col bg-ink-50">
        <Header />
        <main className="flex-1 py-6">
          <Container>{children}</Container>
        </main>
        <Footer />
      </div>
    </>
  );
}

function Header() {
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 border-ink-200 border-b bg-surface">
      <Container>
        <div className="flex h-14 items-center justify-between gap-3">
          <TapTarget
            onClick={() => navigate({ to: "/" })}
            className="flex items-center gap-2 transition-opacity hover:opacity-80"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary font-semibold text-primary-fg text-sm">
              {t.app.brandInitial}
            </div>
            <span className="font-semibold text-ink-900">{t.app.name}</span>
          </TapTarget>

          <UserMenu />
        </div>
      </Container>
    </header>
  );
}

function UserMenu() {
  const me = trpc.auth.me.useQuery();
  const utils = trpc.useUtils();
  const logout = trpc.auth.logout.useMutation({
    onSuccess: () => {
      utils.invalidate();
    },
  });
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);

  if (!me.data) return null;
  const displayName = me.data.displayName ?? me.data.email;

  return (
    <>
      <DropdownMenu
        align="end"
        trigger={
          <TapTarget className="inline-flex max-w-[14rem] items-center gap-2.5 rounded-lg border border-ink-200 bg-surface p-1 pr-3 shadow-subtle transition-colors hover:border-ink-300 hover:bg-ink-50">
            <Avatar name={displayName} size="md" />
            <span className="flex min-w-0 flex-col items-start gap-0.5 text-left">
              <span className="w-full truncate text-[10px] text-ink-500 leading-none">
                {t.appShell.userMenu.signedInAs}
              </span>
              <span className="w-full truncate font-medium text-ink-900 text-xs leading-none">
                {displayName}
              </span>
            </span>
            <Icon name="chevron-down" size="xs" className="shrink-0 text-ink-400" />
          </TapTarget>
        }
      >
        <MenuItem icon="user" onClick={() => setProfileOpen(true)}>
          {t.appShell.userMenu.profileItem}
        </MenuItem>
        <MenuItem icon="credit-card" onClick={() => setCardsOpen(true)}>
          {t.appShell.userMenu.myCardsItem}
        </MenuItem>
        <MenuItem icon="settings" onClick={() => setSettingsOpen(true)}>
          {t.appShell.userMenu.settingsItem}
        </MenuItem>
        <MenuSeparator />
        <MenuItem icon="log-out" onClick={() => logout.mutate()}>
          {t.appShell.logout}
        </MenuItem>
      </DropdownMenu>

      <ProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        email={me.data.email}
        displayName={me.data.displayName ?? ""}
        emailVerified={me.data.emailVerified}
      />

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <MyCardsDialog open={cardsOpen} onClose={() => setCardsOpen(false)} />
    </>
  );
}

function ProfileDialog({
  open,
  onClose,
  email,
  displayName,
  emailVerified,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
  displayName: string;
  emailVerified: boolean;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const update = trpc.auth.updateProfile.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
      onClose();
    },
  });
  const resend = trpc.auth.resendVerification.useMutation({
    onSuccess: () => {
      utils.auth.me.invalidate();
      show({
        tone: "success",
        title: t.appShell.profileDialog.verification.resendSentToast.title,
        description: t.appShell.profileDialog.verification.resendSentToast.description,
        dismissLabel: t.appShell.profileDialog.verification.resendSentToast.dismissLabel,
      });
    },
    onError: (error) => {
      show({
        tone: "danger",
        title: t.appShell.profileDialog.verification.resendFailedToast.title,
        description: error.message,
        dismissLabel: t.appShell.profileDialog.verification.resendFailedToast.dismissLabel,
      });
    },
  });

  const form = useForm({
    defaultValues: { displayName },
    validators: { onChange: profileSchema, onSubmit: profileSchema },
    onSubmit: ({ value }) => {
      const trimmed = value.displayName.trim();
      if (trimmed === displayName.trim()) {
        onClose();
        return;
      }
      update.mutate({ displayName: trimmed });
    },
  });

  // Reset the field whenever the dialog opens — `displayName` may have
  // changed since we last rendered (e.g. another tab updated the profile).
  useEffect(() => {
    if (open) form.reset({ displayName });
  }, [open, displayName, form]);

  const formId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.appShell.profileDialog.title}
      description={t.appShell.profileDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.close}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={update.isPending}>
            {t.common.save}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="displayName">
          {(field) => (
            <Field
              label={t.appShell.profileDialog.displayNameLabel}
              required
              error={fieldError(field)}
            >
              <Input
                autoFocus
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t.appShell.profileDialog.displayNamePlaceholder}
                invalid={!!fieldError(field)}
              />
            </Field>
          )}
        </form.Field>
        <div className="mt-4">
          <Field
            label={t.appShell.profileDialog.emailLabel}
            hint={t.appShell.profileDialog.emailHint}
          >
            <div className="flex flex-col gap-2">
              <VerificationStatus
                verified={emailVerified}
                onResend={() => resend.mutate()}
                resending={resend.isPending}
              />
              <Input value={email} readOnly disabled />
            </div>
          </Field>
        </div>
        {update.isError && (
          <FieldMessage tone="error" className="mt-3 text-sm">
            {t.appShell.profileDialog.saveFailed(update.error.message)}
          </FieldMessage>
        )}
      </form>

      <div className="mt-6 border-ink-200 border-t pt-5 dark:border-ink-300">
        <div className="mb-1 font-medium text-ink-900 text-sm">
          {t.appShell.profileDialog.deleteAccount.sectionTitle}
        </div>
        <p className="mb-3 text-ink-500 text-xs">
          {t.appShell.profileDialog.deleteAccount.sectionDescription}
        </p>
        <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
          {t.appShell.profileDialog.deleteAccount.button}
        </Button>
      </div>

      <DeleteAccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        email={email}
        onDeleted={() => {
          setDeleteOpen(false);
          onClose();
          show({
            tone: "success",
            title: t.appShell.profileDialog.deleteAccount.successToast.title,
            description: t.appShell.profileDialog.deleteAccount.successToast.description,
            dismissLabel: t.appShell.profileDialog.deleteAccount.successToast.dismissLabel,
          });
          utils.invalidate();
        }}
      />
    </Dialog>
  );
}

function DeleteAccountDialog({
  open,
  onClose,
  email,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  email: string;
  onDeleted: () => void;
}) {
  const { show } = useToast();
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const deleteAccount = trpc.auth.deleteAccount.useMutation({
    onSuccess: () => onDeleted(),
    onError: (error) => {
      const isConflict = error.data?.code === "CONFLICT";
      show({
        tone: "danger",
        title: isConflict
          ? t.appShell.profileDialog.deleteAccount.blockedTitle
          : t.appShell.profileDialog.deleteAccount.failedTitle,
        description: isConflict
          ? t.appShell.profileDialog.deleteAccount.blockedDescription
          : error.message,
        dismissLabel: t.common.close,
      });
    },
  });

  const trimmed = typed.trim().toLowerCase();
  const matches = trimmed === email.toLowerCase();
  const showMismatch = trimmed.length > 0 && !matches;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={t.appShell.profileDialog.deleteAccount.confirmTitle}
      description={t.appShell.profileDialog.deleteAccount.confirmDescription}
      footer={
        <>
          <Button onClick={onClose} disabled={deleteAccount.isPending}>
            {t.appShell.profileDialog.deleteAccount.cancelButton}
          </Button>
          <Button
            variant="danger"
            isLoading={deleteAccount.isPending}
            disabled={!matches || deleteAccount.isPending}
            onClick={() => deleteAccount.mutate({ confirmEmail: trimmed })}
          >
            {t.appShell.profileDialog.deleteAccount.deleteButton}
          </Button>
        </>
      }
    >
      <Field
        label={t.appShell.profileDialog.deleteAccount.confirmEmailLabel}
        error={
          showMismatch ? t.appShell.profileDialog.deleteAccount.confirmEmailMismatch : undefined
        }
      >
        <Input
          type="email"
          autoComplete="off"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={t.appShell.profileDialog.deleteAccount.confirmEmailPlaceholder}
          invalid={showMismatch}
        />
      </Field>
    </Dialog>
  );
}

function VerificationStatus({
  verified,
  onResend,
  resending,
}: {
  verified: boolean;
  onResend: () => void;
  resending: boolean;
}) {
  if (verified) {
    return (
      <div className="flex items-start gap-2.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 dark:border-emerald-900/40 dark:bg-emerald-950/30">
        <Icon
          name="check-circle"
          size="sm"
          className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400"
        />
        <div className="text-sm">
          <div className="font-medium text-emerald-900 dark:text-emerald-100">
            {t.appShell.profileDialog.verification.verifiedTitle}
          </div>
          <div className="mt-0.5 text-emerald-800/80 text-xs dark:text-emerald-200/80">
            {t.appShell.profileDialog.verification.verifiedDescription}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/30">
      <Icon
        name="alert-circle"
        size="sm"
        className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
      />
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-medium text-amber-900 dark:text-amber-100">
          {t.appShell.profileDialog.verification.unverifiedTitle}
        </div>
        <div className="mt-0.5 text-amber-800/80 text-xs dark:text-amber-200/80">
          {t.appShell.profileDialog.verification.unverifiedDescription}
        </div>
        <Button size="sm" onClick={onResend} isLoading={resending} className="mt-2">
          {t.appShell.profileDialog.verification.resendButton}
        </Button>
      </div>
    </div>
  );
}

function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { theme, setTheme } = useTheme();
  const { currency, setCurrency } = useCurrency();
  const themeOptions: { value: ThemePref; label: string; icon: "sun" | "moon" | "monitor" }[] = [
    { value: "light", label: t.appShell.settingsDialog.themeOptions.light, icon: "sun" },
    { value: "dark", label: t.appShell.settingsDialog.themeOptions.dark, icon: "moon" },
    { value: "system", label: t.appShell.settingsDialog.themeOptions.system, icon: "monitor" },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.appShell.settingsDialog.title}
      description={t.appShell.settingsDialog.description}
      footer={<Button onClick={onClose}>{t.common.close}</Button>}
    >
      <Field label={t.appShell.settingsDialog.themeLabel}>
        <div className="grid grid-cols-3 gap-2">
          {themeOptions.map((opt) => {
            const active = theme === opt.value;
            return (
              <TapTarget
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                aria-pressed={active}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-md border px-2 py-3 font-medium text-xs transition-colors ${
                  active
                    ? "border-accent-500 bg-accent-50/40 text-ink-900 ring-2 ring-accent-500/20"
                    : "border-ink-200 text-ink-700 hover:border-ink-300 hover:bg-ink-50"
                }`}
              >
                <Icon name={opt.icon} size="sm" />
                {opt.label}
              </TapTarget>
            );
          })}
        </div>
      </Field>
      <div className="mt-4">
        <Field
          label={t.appShell.settingsDialog.currencyLabel}
          hint={t.appShell.settingsDialog.currencyHint}
        >
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)}>
            <option value="NOK">NOK — Norske kroner</option>
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Mine kort dialog — same surface as the /profile/cards route, but opened
 * from the user menu so it stays in the current navigation context. Uses
 * the `MyCardsContent` body (no PageHeader; Dialog renders its own title).
 */
function MyCardsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.cards.profileTitle}
      description={t.cards.profileDescription}
      size="xl"
    >
      <MyCardsContent embedded onClose={onClose} />
    </Dialog>
  );
}
